import "./confirmation-modal.scss";

import * as React from "react";

import {cx} from "emotion";

import {cs}                  from "@Common/react/chain-services";
import {UseState}            from "@Common/react/use-state";
import {Types}               from "@Common/types";
import {formatDate1}         from "@Common/ui-components/format-date";
import {formatMoney}         from "@Common/ui-components/language/format-money";
import {SidebarModalService} from "@Common/ui-components/modal-service-sidebar/modal-service-sidebar";
import {toJsDate}            from "@Common/utils/date-object";

type ConfirmationModalProps = Types.Xpresso.Account.Admin.Listings.ListingFormConfirmationModalProps;
type ModalParams            = Types.Xpresso.Account.Admin.Listings.ListingFormConfirmationModalParams;

export const ConfirmationModal = ({next}:ConfirmationModalProps):JSX.Element => (
    SidebarModalService({
        next,
        className: "confirmation-modal-sidebar-93f3",
        render: (params, setParams) => ({
            content: renderContent(params, setParams),
        }),
    })
);

const renderContent = ({agent, agentAddress, branch, confirmLabel, listing, onConfirm, title}:ModalParams, setParams:(show:ModalParams) => void):JSX.Element => cs(
    ["waitingState", ({}, next) => UseState({next, initValue: false})],
    ({waitingState: {onChange: setWaiting, value: waiting}}) => (
        <div className="confirmation-modal-93f3" xd-qa-type="modal" xd-qa-name="Confirmation Modal">
            <div className="header">
                <h3>{title}</h3>
                <button disabled={waiting} onClick={() => setParams(null)} className="close" xd-qa-type="button" xd-qa-name="Close"></button>
            </div>

            <section>
                <h4>Contact Info</h4>
                {makeTable([
                    {label: "First",    value: agent.agentNameFirst},
                    {label: "Last",     value: agent.agentNameLast},
                    {label: "Address",  value: agentAddress?.address1 ?? ""},
                    {label: "City",     value: agentAddress?.city ?? ""},
                    {label: "State",    value: agentAddress?.state ?? ""},
                    {label: "Zip Code", value: agentAddress?.postalCode ?? ""},
                    {label: "Email",    value: agent.agentEmail},
                ], "Confirm Contact Info")}
            </section>

            <hr/>

            <section>
                <h4>Office Info</h4>
                {makeTable([
                    {label: "Address",  value: branch.branchAddress1},
                    {label: "City",     value: branch.branchCity},
                    {label: "State",    value: branch.branchState},
                    {label: "Zip Code", value: branch.branchZip},
                ], "Confirm Office Info")}
            </section>

            <hr/>

            <section>
                <h4>Location / Recipient Data</h4>
                {makeTable([
                    {label: "Listing Address",     value: listing.locationStreetAddress},
                    {label: "Unit #",              value: listing.locationUnitNumber},
                    {label: "City",                value: listing.locationCity},
                    {label: "State",               value: listing.locationState},
                    {label: "Zip Code",            value: listing.locationZip},
                    {label: "Type",                value: listing.listingType === "jl" ? "Just Listed" : "Just Sold"},
                    {label: "Price",               value: formatMoney(listing.listingType === "jl" ? listing.listedPrice : listing.soldPrice)},
                    {label: "Date Listed",         value: listing.dateListed ? formatDate1(toJsDate(listing.dateListed)) : ""},
                    {label: "Date Sold or Closed", value: listing.dateSold ? formatDate1(toJsDate(listing.dateSold)) : ""},
                    {label: "MLS ID",              value: listing.mlsId},
                    {label: "MLS Name",            value: listing.mlsName},
                    {label: "Open House Date",     value: listing.openHouseDate ? formatDate1(toJsDate(listing.openHouseDate)) : ""},
                    {
                        label: "Open House Time",
                        value: (
                            listing.openHouseStartTime
                                ? `${formatTimeValue(listing.openHouseStartTime)} - ${formatTimeValue(listing.openHouseEndTime)}`
                                : ""
                        )
                    },
                ], "Confirm Location / Recipient Data")}
            </section>

            <hr/>

            <section>
                <h4>Basic Details</h4>
                {makeTable([
                    {label: "Listing Email",              value: agent.agentEmail},
                    {label: "Listing URL",                value: listing.listingUrl,     href: listing.listingUrl, className: "single-line"},
                    {label: "Buyer/Recipient First Name", value: listing.primaryBuyerFirstName},
                    {label: "Buyer/Recipient Last Name",  value: listing.primaryBuyerLastName},
                    {label: "Title",                      value: listing.title},
                    {label: "Property Type",              value: listing.propertyType},
                    {label: "Description",                value: listing.description, className: "description"},
                    {label: "Virtual Tour URL",           value: listing.virtualTourUrl, href: listing.virtualTourUrl, className: "single-line"},
                    {label: "Bedrooms",                   value: listing.bedrooms.toString()},
                    {label: "Room Count",                 value: listing.roomCount.toString()},
                    {label: "Live Area Square Feet",      value: listing.lotSizeSquareFeet},
                    {label: "Lot Size",                   value: listing.lotSize},
                    {label: "Year Built",                 value: listing.yearBuilt},
                    {label: "Fireplace",                  value: listing.firePlace ? "Yes" : "No"},
                ], "Confirm Basic Details")}
            </section>

            <hr/>

            <section>
                <h4>Property Photos</h4>
                {makeTable(new Array(10).fill(null).map((...[,index]) => (
                    {label: `Picture URL ${index + 1}`, href: listing[`picture${index}`], value: listing[`picture${index}`], className: "single-line"}
                )), "Confirm Property Photos")}
            </section>

            <div className="ui">
                <button
                    disabled={waiting}
                    onClick={async () => {setWaiting(true); await onConfirm(); setWaiting(false)}}
                    className="primary"
                    xd-qa-type="button"
                    xd-qa-name={confirmLabel}
                >
                    {confirmLabel}
                </button>
                <button disabled={waiting} onClick={() => setParams(null)} xd-qa-type="button" xd-qa-name="Cancel">Cancel</button>
            </div>
        </div>
    ),
);

const formatTimeValue = (val:number):string => (
    new Date(val).toLocaleTimeString("en-US", {timeZone: "UTC"})
);

interface TableRowProps {
    className ?: string,
    href      ?: string,
    label      : string,
    value      : JSX.Element,
}

const makeTable = (rows:Array<TableRowProps>, xdQaName):JSX.Element => (
    <div className="table" xd-qa-type="card" xd-qa-name={xdQaName}>
        {rows.map(makeTableRow)}
    </div>
);

const makeTableRow = ({className, href, label, value}:TableRowProps):JSX.Element => (
    <div className={cx("row", className)} key={label}>
        <div className="label">{label}</div>
        <div className="value" xd-qa-type="label" xd-qa-name={label} title={typeof value === "string" ? value : undefined}>
            {href ? <a href={href} target="_blank" xd-qa-type="link" xd-qa-name={label}>{value}</a> : value}
        </div>
    </div>
);

